import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "./adminApi";
import { Loader2, Plus, Pencil, Trash2, ExternalLink, Save, X } from "lucide-react";

const EMPTY_PAPER = {
  title: "",
  authors: "",
  venue: "",
  year: new Date().getFullYear(),
  type: "Journal",
  tag: "",
  href: "",
};

const PapersPanel = () => {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { ...paper } or EMPTY_PAPER
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/content/papers");
      setPapers(res.data?.papers || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load papers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openCreate = () => {
    setError("");
    setEditing({ ...EMPTY_PAPER });
  };

  const openEdit = (p) => {
    setError("");
    setEditing({ ...p });
  };

  const handleSave = async () => {
    if (!editing.title.trim() || !editing.authors.trim()) {
      setError("Title and authors are required.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const payload = { ...editing, year: parseInt(editing.year, 10) || 0 };
      if (editing.id) {
        await adminApi.put(`/admin/content/papers/${editing.id}`, payload);
      } else {
        await adminApi.post("/admin/content/papers", payload);
      }
      setEditing(null);
      fetchAll();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save paper.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.id);
    try {
      await adminApi.delete(`/admin/content/papers/${confirmDelete.id}`);
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete.");
    } finally {
      setDeletingId(null);
    }
  };

  const byYear = papers.reduce((acc, p) => {
    (acc[p.year] = acc[p.year] || []).push(p);
    return acc;
  }, {});
  const years = Object.keys(byYear).sort((a, b) => b - a);

  return (
    <div className="space-y-6" data-testid="admin-papers-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-light text-gray-900 tracking-tight">Publications</h2>
          <p className="text-sm text-gray-500 font-light mt-1">
            Manage the paper archive. Public site shows only 2025 &amp; 2026 entries.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 text-sm bg-gray-900 hover:bg-black text-white px-4 py-2.5 rounded-md transition-colors"
          data-testid="admin-add-paper"
        >
          <Plus className="w-4 h-4" />
          Add paper
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : papers.length === 0 ? (
        <EmptyState message="No papers yet. Add your first paper." />
      ) : (
        <div className="space-y-8">
          {years.map((y) => (
            <div key={y}>
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-3xl font-light text-gray-900 tracking-tight">{y}</span>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-gray-400">
                  {byYear[y].length} paper{byYear[y].length !== 1 ? "s" : ""}
                </span>
                {(y === "2025" || y === "2026") && (
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Public
                  </span>
                )}
              </div>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {byYear[y].map((p, i) => (
                  <div
                    key={p.id}
                    className={`px-5 py-4 flex items-start gap-4 hover:bg-gray-50 transition-colors ${
                      i > 0 ? "border-t border-gray-100" : ""
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 leading-snug">
                        {p.title}
                      </div>
                      <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                        <span>{p.authors}</span>
                        {p.venue && <span className="text-gray-400">·</span>}
                        {p.venue && <span className="text-gray-500">{p.venue}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {p.tag && (
                          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-gray-400">
                            {p.tag}
                          </span>
                        )}
                        {p.href && (
                          <a
                            href={p.href}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] text-[#00BCD4] hover:underline inline-flex items-center gap-1"
                          >
                            View source <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => openEdit(p)}
                        className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                        aria-label="Edit paper"
                        data-testid={`edit-paper-${p.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(p)}
                        disabled={deletingId === p.id}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                        aria-label="Delete paper"
                        data-testid={`delete-paper-${p.id}`}
                      >
                        {deletingId === p.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PaperEditModal
          paper={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
          saving={saving}
          error={error}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          label="paper"
          item={confirmDelete}
          preview={confirmDelete.title}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={handleDelete}
          loading={deletingId === confirmDelete.id}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const PaperEditModal = ({ paper, onChange, onCancel, onSave, saving, error }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    onClick={onCancel}
  >
    <div
      className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative bg-gray-900 px-6 py-5 flex-shrink-0">
        <button
          onClick={onCancel}
          className="absolute right-4 top-4 text-white/70 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60 mb-2">
          {paper.id ? "Edit paper" : "New paper"}
        </div>
        <h2 className="text-2xl font-light text-white tracking-tight">
          Publication details
        </h2>
      </div>

      <div className="p-6 sm:p-7 overflow-y-auto space-y-4">
        <Field label="Title" required value={paper.title} onChange={(v) => onChange({ ...paper, title: v })} testid="paper-title" />
        <Field label="Authors" required value={paper.authors} onChange={(v) => onChange({ ...paper, authors: v })} placeholder="Last F., Last F." testid="paper-authors" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Year" required type="number" value={paper.year} onChange={(v) => onChange({ ...paper, year: v })} testid="paper-year" />
          <div>
            <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-600 block mb-1.5">Type</label>
            <select
              value={paper.type}
              onChange={(e) => onChange({ ...paper, type: e.target.value })}
              className="w-full px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 font-light"
              data-testid="paper-type"
            >
              <option>Journal</option>
              <option>Conference</option>
              <option>Preprint</option>
              <option>Chapter</option>
            </select>
          </div>
        </div>
        <Field label="Venue" value={paper.venue} onChange={(v) => onChange({ ...paper, venue: v })} placeholder="Nature Machine Intelligence" testid="paper-venue" />
        <Field label="Tag / Domain" value={paper.tag} onChange={(v) => onChange({ ...paper, tag: v })} placeholder="Biomedical AI" testid="paper-tag" />
        <Field label="Link (DOI / URL)" value={paper.href || ""} onChange={(v) => onChange({ ...paper, href: v })} placeholder="https://doi.org/…" testid="paper-href" />

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}
      </div>

      <div className="px-6 sm:px-7 py-4 border-t border-gray-100 flex items-center justify-end gap-2 flex-shrink-0">
        <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-900 px-3 py-2">Cancel</button>
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-gray-900 hover:bg-black text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors disabled:opacity-60"
          data-testid="save-paper"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
      </div>
    </div>
  </div>
);

const Field = ({ label, required, type = "text", value, onChange, placeholder, testid }) => (
  <div>
    <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-600 block mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 font-light"
      data-testid={testid}
    />
  </div>
);

const EmptyState = ({ message }) => (
  <div className="text-center py-20 bg-white border border-dashed border-gray-200 rounded-xl">
    <p className="text-sm text-gray-500 font-light">{message}</p>
  </div>
);

const ConfirmDeleteModal = ({ label, preview, onCancel, onConfirm, loading }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    onClick={onCancel}
  >
    <div
      className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-red-600 px-6 py-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/70 mb-2">
          Delete {label}
        </div>
        <h2 className="text-xl font-light text-white tracking-tight">
          Remove this {label}?
        </h2>
      </div>
      <div className="p-6">
        <p className="text-sm text-gray-600 font-light mb-4">
          This action cannot be undone.
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-700">
          {preview}
        </div>
        <div className="flex items-center justify-end gap-2 mt-6">
          <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-900 px-3 py-2">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default PapersPanel;
export { ConfirmDeleteModal, Field, EmptyState };
